import Shimmer from "@/components/Shimmer";
import {
  Bold,
  HeaderTextBlock,
  P,
  TypographyContainer,
} from "@/components/Typography";
import {
  useGetBilling,
  useReprocessSubscription,
  useRestartSubscription,
  useUser,
} from "@/requests/hooks/user";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import useIsMobile from "@/utils/useIsMobile.ts";
import { Button } from "@/components/Button";
import { MAILTO_SUPPORT } from "@/utils/const";
import { useTracking } from "@/requests/hooks/app.ts";
import { Alert } from "@/components/Alert";
import {
  faArrowUpRightFromSquare,
  faFileInvoiceDollar,
  faPlusCircle,
  faSpinnerThird,
  faTriangleExclamation,
  faUserRobotXmarks,
} from "@fortawesome/pro-duotone-svg-icons";
import stringToTitleCase from "@/utils/stringToTitleCase";
import parseQueryString from "@/utils/parseQueryString";
import shopifyLogo from "@/assets/images/collect/shopify-logo.png";
import { CancelSubscriptionModal } from "@/components/Modal";
import { Card } from "@/components/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";

const Billing = () => {
  const { t } = useTranslation();
  const { mutate: sendTracking } = useTracking();
  const isMobile = useIsMobile();

  const { data: user, refetch: refetchUser } = useUser();
  const {
    isPending: isGetBillingPending,
    data: billing,
    refetch: refetchBilling,
  } = useGetBilling();
  const {
    isPending: isRestartSubscriptionPending,
    mutate: restartSubscription,
  } = useRestartSubscription();
  const { mutate: reprocessSubscription } = useReprocessSubscription();
  const { charge_id: chargeId } = parseQueryString();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  let authRetryCounter = 0;

  useEffect(() => {
    if (!billing) return;

    const items = billing?.subscription?.items;
    const collectSubscription = items?.find(sub => sub.product === "COLLECT");

    if (chargeId) {
      sendTracking({
        eventName: "control-panel.billing.approve-subscription",
        additionalProperties: {
          subscription_name: collectSubscription?.tier?.toLowerCase(),
        },
      });
    }
  }, [billing]);

  useEffect(() => {
    if (!billing) return;

    const { subscription, upcomingSubscription } = billing;
    const collectSubscription = subscription?.items?.find(
      sub => sub.product === "COLLECT",
    );
    if (!!upcomingSubscription && upcomingSubscription.status === "DECLINED") {
      sendTracking({
        eventName: "control-panel.billing.decline-subscription",
        additionalProperties: {
          subscription_name: collectSubscription?.tier?.toLowerCase(),
        },
      });
    }
  }, [billing]);

  useEffect(() => {
    if (!user?.scopes?.includes("DELINQUENT_ROAD_BLOCK")) return;

    if (chargeId && authRetryCounter < 5) {
      setTimeout(() => refetchUser(), 2000);
      authRetryCounter++;
    }
  }, [user]);

  const toggleCancelSubscriptionModal = () => {
    setIsModalOpen(isOpen => !isOpen);
  };

  const onClickRestartSubscription = () => {
    restartSubscription("COLLECT");
  };

  const onClickReprocessSubscription = () => {
    if (billing?.upcomingSubscription?.id !== undefined)
      reprocessSubscription(billing?.upcomingSubscription?.id);
  };

  const getCollectSubscription = () => {
    if (!billing) return;

    const { subscription: currentSubscription, upcomingSubscription } = billing;
    const collectSubscription = currentSubscription?.items?.find(
      sub => sub.product === "COLLECT",
    );
    const collectUpcomingSubscription = upcomingSubscription?.items?.find(
      sub => sub.product === "COLLECT",
    );
    return collectSubscription || collectUpcomingSubscription;
  };

  const isZeroPricedSubscription = getCollectSubscription()?.price === 0;

  const renderHeaderTextBlock = () => (
    <TypographyContainer>
      <HeaderTextBlock
        align="left"
        featureText={t("settings.billing.header.feature")}
        headingText={t("settings.billing.header.heading")}
      />
    </TypographyContainer>
  );

  const renderSuccessAlert = (tier?: ISubscriptionTier) => (
    <div className="mb-30">
      <Alert
        variant="success"
        titleSlot={t("settings.billing.alerts.success.title")}>
        <P>
          {t("settings.billing.alerts.success.description", {
            tier: stringToTitleCase(tier),
          })}
        </P>
      </Alert>
    </div>
  );

  const renderLoadingAlert = () => (
    <div className="mb-30">
      <Alert
        variant="info"
        titleSlot={t("settings.billing.alerts.loading.title")}
        icon={faSpinnerThird}
        iconClassName="animate-spin">
        <P>{t("settings.billing.alerts.loading.description")}</P>
      </Alert>
    </div>
  );

  const renderDelayAlert = () => (
    <div className="mb-30">
      <Alert
        icon={faUserRobotXmarks}
        titleSlot={t("settings.billing.alerts.delay.title")}>
        <Trans i18nKey="settings.billing.alerts.delay.description">
          zero
          <Button
            as="a"
            variant="link"
            href={MAILTO_SUPPORT}
            trackingId="support.mailto"
            target="_blank">
            one
          </Button>
          two
        </Trans>
      </Alert>
    </div>
  );

  const renderDeclinedAlert = () => (
    <div className="mb-30">
      <Alert
        icon={faTriangleExclamation}
        titleSlot={<Bold>{t("settings.billing.alerts.declined.title")}</Bold>}
        variant="error"
        testId="declined-billing-alert">
        <>
          <div className="mb-10">
            <P>{t("settings.billing.alerts.declined.description")}</P>
          </div>
          <div className="flex flex-row flex-wrap">
            <Button
              type="button"
              variant="white-background"
              onClick={onClickReprocessSubscription}
              trackingId="control-panel.billing.reprocess-subscription">
              {t("settings.billing.alerts.declined.cta")}
            </Button>
          </div>
        </>
      </Alert>
    </div>
  );

  const renderApprovalRequiredAlert = (confirmationUrl: string) => {
    const { billingSystem } = billing as IGetBillingRS;
    const subscription = getCollectSubscription();

    return (
      <div className="mb-30">
        <Alert
          titleSlot={
            <Bold>
              {t("settings.billing.alerts.approvalRequired.title", {
                billingSystem: stringToTitleCase(billingSystem),
              })}
            </Bold>
          }
          variant="warning"
          icon={faTriangleExclamation}>
          <div className="mb-10">
            <P>
              {t("settings.billing.alerts.approvalRequired.description", {
                billingSystem: stringToTitleCase(billingSystem),
              })}
            </P>
          </div>
          <Button
            as="a"
            variant="white-background"
            icon={faArrowUpRightFromSquare}
            iconColor="text-primary"
            iconClassName="h-15"
            iconPosition="end"
            href={confirmationUrl}
            target="_blank"
            trackingId={{
              eventName: "control-panel.billing.access-approve-subscription",
              additionalProperties: {
                subscription_name: subscription?.tier.toLowerCase(),
              },
            }}>
            {t("settings.billing.alerts.approvalRequired.cta")}
          </Button>
        </Alert>
      </div>
    );
  };

  const renderBillingAlert = () => {
    const { subscription: currentSubscription, upcomingSubscription } =
      billing as IGetBillingRS;
    const subscription = currentSubscription || upcomingSubscription;

    if (!!upcomingSubscription && upcomingSubscription.status === "DECLINED")
      return renderDeclinedAlert();

    if (!subscription) return null;

    const { status, confirmationUrl } = subscription;
    const collectSubscription = subscription?.items?.find(({ tier }) => !!tier);

    const isActiveOrPending = status === "ACTIVE" || status === "PENDING";
    const hasDelinquentRoadBlock = user?.scopes?.includes(
      "DELINQUENT_ROAD_BLOCK",
    );
    const paidButDelinquent =
      chargeId && isActiveOrPending && hasDelinquentRoadBlock;

    if (paidButDelinquent && authRetryCounter < 5) {
      return renderLoadingAlert();
    }

    if (paidButDelinquent && authRetryCounter >= 5) {
      return renderDelayAlert();
    }

    if (chargeId && isActiveOrPending && !hasDelinquentRoadBlock) {
      return renderSuccessAlert(collectSubscription?.tier);
    }

    if (confirmationUrl && status === "PENDING") {
      return renderApprovalRequiredAlert(confirmationUrl);
    }

    return null;
  };

  const getShopifyPlanName = (price: number | undefined) => {
    if (!price) return "Free Trial";

    if (price === 0) {
      return t("settings.billing.subscriptionStatusCard.freeTrial");
    } else if (price > 0 && price <= 500) {
      return t("settings.billing.subscriptionStatusCard.starter");
    } else if (price > 500 && price <= 1500) {
      return t("settings.billing.subscriptionStatusCard.plus");
    } else if (price > 1500 && price <= 3000) {
      return t("settings.billing.subscriptionStatusCard.pro");
    } else {
      return t("settings.billing.subscriptionStatusCard.custom");
    }
  };

  const renderNoSubscriptions = () => (
    <Alert testId="no-subscription-section">
      <Trans i18nKey="settings.billing.noSubscription">
        zero
        <Button
          as="a"
          variant="link"
          href={MAILTO_SUPPORT}
          trackingId="support.mailto"
          target="_blank">
          one
        </Button>
        two
      </Trans>
    </Alert>
  );

  const renderRestartSubscription = () => (
    <>
      <Alert>
        <P>{t("settings.billing.restartSubscription.description")}</P>
        <P>
          <span>{t("settings.billing.restartSubscription.support")}</span>
          <Button
            as="a"
            variant="link"
            href={MAILTO_SUPPORT}
            trackingId="support.mailto"
            target="_blank">
            {t("settings.billing.restartSubscription.supportLink")}
          </Button>
        </P>
      </Alert>
      <div className="mt-30 flex justify-center">
        <Button
          as="button"
          type="button"
          variant="primary"
          trackingId="settings.billing.restart-subscription"
          loading={isRestartSubscriptionPending}
          onClick={onClickRestartSubscription}
          icon={faPlusCircle}
          iconClassName="h-20"
          iconColor="#ffffff">
          {t("settings.billing.restartSubscription.cta")}
        </Button>
      </div>
    </>
  );

  const renderSubscriptionTitle = () => (
    <div className="mb-10 flex flex-row">
      <FontAwesomeIcon
        icon={faFileInvoiceDollar}
        className="mr-20 h-20 w-20 text-primary"
      />
      <Bold>{t("settings.billing.subscriptionStatusCard.title")}</Bold>
    </div>
  );
  const renderProductTitleSection = (product?: ISubscriptionProduct) => (
    <div className="text-secondary">{stringToTitleCase(product)}</div>
  );

  const renderVerifiedContactsSection = (
    verifiedContacts?: number,
    tierThreshold?: number,
  ) => (
    <div className="flex flex-row flex-wrap">
      <Trans
        i18nKey="settings.billing.subscriptionStatusCard.verifiedContacts"
        values={{
          verifiedContacts: verifiedContacts?.toLocaleString(),
          tierThreshold: tierThreshold?.toLocaleString(),
        }}>
        zero
        <Bold variant="secondary">one</Bold>
        two
        <Bold variant="secondary">three</Bold>
      </Trans>
    </div>
  );

  const renderTierSection = () => (
    <div className="flex flex-row flex-wrap">
      <Trans
        i18nKey="settings.billing.subscriptionStatusCard.currentPlan"
        values={{ tier: getShopifyPlanName(getCollectSubscription()?.price) }}>
        zero
        <div className="text-secondary">one</div>
      </Trans>
    </div>
  );

  const renderSubscriptionCard = (
    product: ISubscriptionProduct,
    subscription: ISubscription,
  ) => {
    if (product === "COMPLY") return null;

    const { verifiedContacts, tierThreshold } = subscription;

    return (
      <div className="mb-30" key={product}>
        <Card backgroundColor="white" shadow={true} additionalClassName="p-20">
          <>
            {renderSubscriptionTitle()}
            <div className="ml-40 flex flex-col flex-wrap gap-10 whitespace-pre-wrap text-gray-dark">
              {renderProductTitleSection(product)}
              {renderVerifiedContactsSection(verifiedContacts, tierThreshold)}
              {renderTierSection()}
            </div>
          </>
        </Card>
      </div>
    );
  };

  const renderBillingDetailsContent = () => {
    const {
      billingSystem,
      settingsUrl,
      subscription: currentSubscription,
      upcomingSubscription,
    } = billing as IGetBillingRS;
    const subscription = currentSubscription || upcomingSubscription;

    const hasStarterSubscription = subscription?.items?.some(
      item => item.tier === "STARTER",
    );

    if (hasStarterSubscription)
      return (
        <P>{t("settings.billing.billingDetailsCard.starterDescription")}</P>
      );

    return (
      <div className="flex flex-row items-center gap-5">
        <P>
          {t("settings.billing.billingDetailsCard.descriptionStart", {
            provider: stringToTitleCase(billingSystem),
          })}
        </P>
        <Button
          as="a"
          variant="link"
          trackingId="control-panel.billing.access-external-billing"
          target="_blank"
          href={settingsUrl}>
          {t("settings.billing.billingDetailsCard.descriptionLinkTitle")}
        </Button>
        <P>{t("settings.billing.billingDetailsCard.descriptionEnd")}</P>
      </div>
    );
  };

  const renderBillingDetailsCard = () => {
    if (!billing || billing?.billingSystem === "NONE") return null;

    return (
      <Card backgroundColor="white" shadow={true} additionalClassName="p-20">
        <div className="mb-10 flex flex-row">
          <img src={shopifyLogo} className="mr-20 h-20 w-20" />
          <Bold>{t("settings.billing.billingDetailsCard.title")}</Bold>
        </div>
        <div className="ml-40 text-secondary">
          {renderBillingDetailsContent()}
        </div>
      </Card>
    );
  };

  const renderButtons = () => {
    const collectSubscription = getCollectSubscription();

    return (
      <div className="flex flex-col gap-30 max-sm:items-center max-sm:justify-center md:flex-row">
        <Button
          as="link"
          type="button"
          variant="secondary-outline"
          trackingId="support.access-customer-support-form"
          width={isMobile ? "full" : "lg"}
          to="/support">
          {t("settings.billing.cta.customerSupport")}
        </Button>
        {(billing?.subscription || billing?.upcomingSubscription) &&
          !isZeroPricedSubscription && (
            <Button
              as="button"
              type="button"
              variant="link"
              icon={faCaretRight}
              iconClassName="h-15"
              iconColor=" text-secondary"
              iconPosition="end"
              onClick={toggleCancelSubscriptionModal}
              trackingId={{
                eventName: "control-panel.billing.request-cancel-subscription",
                additionalProperties: {
                  subscription_name: collectSubscription?.tier?.toLowerCase(),
                },
              }}>
              {t("settings.billing.cta.cancelSubscription")}
            </Button>
          )}
      </div>
    );
  };
  const renderCancelSubscriptionModal = () => (
    <CancelSubscriptionModal
      isOpen={isModalOpen}
      toggleIsOpen={toggleCancelSubscriptionModal}
      billing={billing}
      refetchBilling={refetchBilling}
    />
  );

  const renderSubscriptions = () => {
    const {
      billingSystem,
      subscription: currentSubscription,
      upcomingSubscription,
    } = billing as IGetBillingRS;
    const subscription = currentSubscription || upcomingSubscription;

    if (billingSystem === "SHOPIFY") {
      return subscription?.items.map(({ product }) =>
        renderSubscriptionCard(product, subscription),
      );
    }
  };

  const renderBillingAndSubscriptions = () => (
    <>
      {renderBillingAlert()}

      {renderSubscriptions()}

      <div className="mb-30">{renderBillingDetailsCard()}</div>

      {renderButtons()}
    </>
  );

  const renderBody = () => {
    if (isGetBillingPending)
      return (
        <div className="flex flex-col gap-30">
          <Shimmer width="100%" height="185px" />
          <Shimmer width="100%" height="155px" />
        </div>
      );

    if (billing?.billingSystem === "SHOPIFY" && billing?.upcomingSubscription)
      return renderBillingAndSubscriptions();

    if (billing?.billingSystem === "SHOPIFY" && !billing?.subscription)
      return renderRestartSubscription();

    if (
      billing?.billingSystem === "NONE" ||
      !billing?.subscription ||
      !billing ||
      !billing?.billingSystem
    )
      return renderNoSubscriptions();

    return renderBillingAndSubscriptions();
  };

  return (
    <>
      <>
        {renderHeaderTextBlock()}
        {renderBody()}
      </>
      {renderCancelSubscriptionModal()}
    </>
  );
};

export default Billing;
